import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";

// Lazy-loaded pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const DigitalReceipt = lazy(() => import("./pages/DigitalReceipt"));
const DigitalShowcase = lazy(() => import("./pages/DigitalShowcase"));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const SuperAdmin = lazy(() => import("./pages/SuperAdmin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Kvkk = lazy(() => import("./pages/Kvkk"));

// Lazy-loaded authenticated app shell (includes heavy providers + all protected routes)
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Global defaults:
// - staleTime 30s → fewer refetches, snappier navigation
// - queries retry once for transient network errors, never for 4xx auth failures
// - mutations never auto-retry → prevents double-charging on flaky networks
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: "always",
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number; code?: string })?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Lightweight public routes — no heavy providers */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/receipt" element={<DigitalReceipt />} />
            <Route path="/showcase" element={<DigitalShowcase />} />
            <Route path="/showcase/:userId" element={<DigitalShowcase />} />
            <Route path="/portal/:shopId" element={<CustomerPortal />} />
            <Route path="/superadmin-stok" element={<SuperAdmin />} />
            <Route path="/kvkk" element={<Kvkk />} />

            {/* All other routes go through AuthenticatedApp (lazy-loaded with providers) */}
            <Route path="/*" element={<AuthenticatedApp />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
